import styled, { css } from 'styled-components';
import { remCalc, colors, layout, media } from '@prisjakt/ui-base';
import LogoComponent from '@prisjakt/ui-logo';
import NewsletterSignupContainer from '@prisjakt/ui-newsletter-signup';
import InfoPagesComponent from './components/info-pages';
import FeedbackButtonComponent from '../feedback/feedback-button';
import { InfoPagesStyleProps } from './types';

const logoWidth = '180px';
const borderColor = colors.lightBlue1;
const sharedFooterStyling = css`
  padding-top: ${layout.spacing.small};
  padding-bottom: ${layout.spacing.small};

  ${media.smallOnly`
    padding: ${layout.spacing.small} ${layout.spacing.large};
  `};
`;
const buttonWidth = '232px';

const {
  pageContentMaxWidthVal,
  sidebarMenuWidth,
  sidebarContentWidth,
} = layout;

const pageWithSidebarContent =
  pageContentMaxWidthVal + sidebarMenuWidth.desktop.value * 2;

interface FooterProps {
  sidebarOpen?: boolean;
}

export const Footer = styled.footer<FooterProps>`
  transition: all 200ms ease;

  /* Offset footer to the left behind the invisible sidebar wrapper
   to keep the footer full width */
  @media screen and (min-width: ${pageWithSidebarContent}px) {
    margin-left: ${props =>
      props.sidebarOpen
        ? 0
        : `calc(((1250px - 100vw) / 2) + ${sidebarMenuWidth.desktop.rem})`};
  }

  @media screen and (min-width: 1980px) {
    margin-left: ${props =>
      props.sidebarOpen
        ? 0
        : `calc(-1 * ${layout.sidebarContentWidth.desktop.rem})`};
  }
`;

export const Content = styled.div<FooterProps>`
  max-width: ${layout.pageContent.maxWidth.rem};
  display: flex;
  width: 100%;
  transition: all 200ms ease;

  padding-left: ${layout.spacing.xSmall};
  padding-right: ${layout.spacing.xSmall};

  /* Align footer content with the rest of the page */
  @media screen and (min-width: ${pageWithSidebarContent}px) {
    margin-left: ${props =>
      props.sidebarOpen
        ? 'inherit'
        : `calc(((100vw - 1250px) / 2) - ${sidebarMenuWidth.desktop.rem})`};
  }

  @media screen and (min-width: 1980px) {
    margin-left: ${props =>
      props.sidebarOpen
        ? `calc(((100vw - 1250px) / 2) - ${sidebarMenuWidth.desktop.value +
            sidebarContentWidth.desktop.value}px)`
        : `calc(((100vw - 1250px) / 2) - ${sidebarMenuWidth.desktop.rem})`};
  }
`;

export const TopSection = styled.div`
  ${sharedFooterStyling};
  padding: ${layout.spacing.small} ${layout.spacing.medium};
  background: ${p => p.theme.footer.bg.secondary};

  ${Content} {
    justify-content: space-between;

    ${media.smallOnly`
      flex-direction: column;
      align-items: center;
    `}
  }
`;

export const MidSection = styled.div`
  ${sharedFooterStyling};
  padding-top: ${layout.spacing.medium};
  padding-bottom: 0;
  justify-content: space-between;
  background: ${p => p.theme.footer.bg.primary};
  color: ${p => p.theme.footer.fg.primary};

  ${Content} {
    padding: 0 ${layout.spacing.medium};

    ${media.smallOnly`
      flex-direction: column;
      align-items: center;
      padding: 0;
    `};
  }

  ${media.smallOnly`
    padding-bottom: 0;
  `};
`;

export const BottomSection = styled.div`
  ${sharedFooterStyling};
  background: ${p => p.theme.footer.bg.primary};
  color: ${p => p.theme.footer.fg.primary};
  padding-bottom: ${layout.spacing.large};
  padding-top: ${layout.spacing.medium};

  ${Content} {
    display: flex;
    flex-direction: column;
    align-items: center;
    border-top: 1px solid ${borderColor};
    padding-top: ${layout.spacing.medium};
  }

  ${media.smallOnly`
    padding-top: ${layout.spacing.xSmall};
  `};
`;

export const Logo = styled(LogoComponent)`
  margin-bottom: 10px;
  width: ${logoWidth};
  max-width: 100%;
`;

export const Social = styled.div``;

export const SocialIcon = styled.a`
  color: ${colors.white};
  margin: 0 ${remCalc(4)};
`;

export const LogoWrapper = styled.div`
  display: flex;
  align-items: center;

  ${media.mediumUp`
    ${SocialIcon}:first-child {
        margin-left: ${remCalc(29)};
    }
  `};

  ${media.smallOnly`
    flex-direction: column;
  `};
`;

export const InfoPages = styled(InfoPagesComponent)<InfoPagesStyleProps>`
  display: flex;
  flex-wrap: wrap;
  flex: 1 1 auto;
  justify-content: space-between;
  border-left: 1px solid ${borderColor};
  border-right: 1px solid ${borderColor};
  padding: 0 ${layout.spacing.xLarge};

  ${media.mediumOnly`
    padding-left: ${layout.spacing.small};
    padding-right: ${layout.spacing.small};
  `};

  ${media.smallOnly`
    width: 100%;
    padding-top: ${layout.spacing.small};
    padding-left: 0;
    padding-right: 0;
    border: none;
    border-top: 1px solid ${borderColor};
    border-bottom: 1px solid ${borderColor};
    flex-wrap: wrap;
    display: grid;
    grid-template-columns: ${(p: InfoPagesStyleProps) =>
      `repeat(${p.numberOfColumns}, 1fr)`};
  `};
`;

export const ColumnTitle = styled.h4`
  font-size: ${layout.fontSizes.base.desktop.rem};
  font-weight: 600;
  margin-bottom: ${layout.spacing.small};
`;

export const Download = styled.div`
  min-width: 128px;
  margin: 0 0 0 ${layout.spacing.large};
  text-align: center;

  ${media.mediumOnly`
      margin-left: ${layout.spacing.medium};
    `};

  ${media.smallOnly`
      margin: 0;
      margin-top: ${layout.spacing.small};
    `};
`;

export const DownloadIcons = styled.div`
  ${media.smallOnly`
    display: flex;

    > :first-child {
      margin-right: ${layout.spacing.xSmall};
    }
  `};
`;

export const Sites = styled.div`
  width: ${buttonWidth};
`;

export const NewsletterSignup = styled(NewsletterSignupContainer)`
  width: 100%;
  max-width: ${buttonWidth};
  margin: 0 ${layout.spacing.large} 0 0;

  ${media.smallOnly`
    margin: 0 0 ${layout.spacing.small} 0;
  `};

  ${media.mediumOnly`
    margin-right: ${layout.spacing.medium};
  `};
`;

export const FeedbackButton = styled(FeedbackButtonComponent)`
  width: ${buttonWidth};

  ${media.smallOnly`
    margin-bottom: ${layout.spacing.small};
  `}
`;

export const ButtonText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
